import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import language from '../../variables/language';
import IconAdd from '../../components/icons/IconAdd';

// Same tokenizer as PhraseService.tokenize on the backend (letters + "'" only,
// everything else is a separator) - kept in sync deliberately so the live
// preview colors line up with what actually gets linked on save. This is a
// preview aid only though: the backend re-tokenizes req.text itself and is
// the actual source of truth for what gets saved as WordPhrase links.
const TOKEN_RE = /[A-Za-z']+/g;

const splitSegments = (text) => {
   const segments = [];
   let lastIndex = 0;
   let m;
   TOKEN_RE.lastIndex = 0;
   while ((m = TOKEN_RE.exec(text))) {
      if (m.index > lastIndex) segments.push({ type: 'sep', value: text.slice(lastIndex, m.index) });
      segments.push({ type: 'word', value: m[0] });
      lastIndex = m.index + m[0].length;
   }
   if (lastIndex < text.length) segments.push({ type: 'sep', value: text.slice(lastIndex) });
   return segments;
}

// mid-word tokens (nothing typed after them yet) stay neutral - the live
// found/missing check only fires once a word is "finished" (a separator
// follows it), matching the requested behavior exactly.
const lastSegmentIsMidWord = (text) => /[A-Za-z']$/.test(text);

export default function PhraseComposer({ lessonId, studentId, typeId, onAttached }) {
   const langRoot = language[language.lang];
   const lang = langRoot.phrasesTab;

   const [text, setText] = useState('');
   const [translation, setTranslation] = useState('');
   const [wordStatus, setWordStatus] = useState({});
   const [suggestions, setSuggestions] = useState([]);
   const [saving, setSaving] = useState(false);
   const [attachingId, setAttachingId] = useState(null);
   const [availableTags, setAvailableTags] = useState([]);
   const [selectedTagIds, setSelectedTagIds] = useState([]);
   const statusCacheRef = useRef({});

   // tag options are scoped to the active Phrase/Sentence tab - re-fetch (and
   // drop any selection made under the previous type) whenever it changes.
   useEffect(() => {
      setSelectedTagIds([]);
      if (!typeId) { setAvailableTags([]); return; }
      axios.get('/api/phrase-tags', { params: { typeId }, withCredentials: true })
         .then(r => setAvailableTags(Array.isArray(r.data) ? r.data : []))
         .catch(() => setAvailableTags([]));
   }, [typeId]);

   const toggleTag = (tagId) => {
      setSelectedTagIds(prev => prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]);
   }

   useEffect(() => {
      const handle = setTimeout(() => {
         const segments = splitSegments(text);
         const midWord = lastSegmentIsMidWord(text);
         const wordSegments = segments.filter(s => s.type === 'word');
         const completed = midWord ? wordSegments.slice(0, -1) : wordSegments;
         const toCheck = [...new Set(completed.map(s => s.value.toLowerCase()))]
            .filter(tok => statusCacheRef.current[tok] === undefined);

         toCheck.forEach(tok => {
            statusCacheRef.current[tok] = 'checking';
            axios.get('/api/words/lookup', { params: { word: tok }, withCredentials: true })
               .then(() => { statusCacheRef.current[tok] = 'found'; setWordStatus({ ...statusCacheRef.current }); })
               .catch(() => { statusCacheRef.current[tok] = 'missing'; setWordStatus({ ...statusCacheRef.current }); });
         });
         if (toCheck.length > 0) setWordStatus({ ...statusCacheRef.current });

         const q = text.trim();
         if (q.length >= 3) {
            axios.get('/api/phrases/search', { params: { q, typeId }, withCredentials: true })
               .then(r => setSuggestions(Array.isArray(r.data) ? r.data : []))
               .catch(() => setSuggestions([]));
         } else {
            setSuggestions([]);
         }
      }, 250);
      return () => clearTimeout(handle);
   }, [text, typeId]);

   const reset = () => {
      setText('');
      setTranslation('');
      statusCacheRef.current = {};
      setWordStatus({});
      setSuggestions([]);
      setSelectedTagIds([]);
   }

   const handleCreate = () => {
      if (!text.trim() || saving) return;
      setSaving(true);
      const payload = { text: text.trim() };
      if (translation.trim()) payload.translation = translation.trim();
      if (lessonId) payload.lessonId = lessonId;
      if (studentId) payload.studentId = studentId;
      if (typeId) payload.typeId = typeId;
      if (selectedTagIds.length) payload.tagIds = selectedTagIds;
      axios.post('/api/phrases', JSON.stringify(payload), {
         withCredentials: true, headers: { 'Content-Type': 'application/json' },
      }).then(() => {
         reset();
         if (onAttached) onAttached();
      }).catch(err => console.log(err))
        .finally(() => setSaving(false));
   }

   const handleAttachExisting = (phraseId) => {
      if (attachingId) return;
      setAttachingId(phraseId);
      const url = lessonId ? '/api/phrase-lessons' : '/api/student-phrases';
      const body = lessonId ? { lesson: String(lessonId), phrase: String(phraseId) } : { student: String(studentId), phrase: String(phraseId) };
      axios.post(url, JSON.stringify(body), {
         withCredentials: true, headers: { 'Content-Type': 'application/json' },
      }).then(() => {
         reset();
         if (onAttached) onAttached();
      }).catch(err => console.log(err))
        .finally(() => setAttachingId(null));
   }

   const segments = splitSegments(text);
   const midWord = lastSegmentIsMidWord(text);
   const lastWordIndex = (() => {
      let idx = -1;
      segments.forEach((s, i) => { if (s.type === 'word') idx = i; });
      return idx;
   })();

   return (
      <div className="phrase-composer">
         <div className="phrase-composer-input-row">
            <input className="phrase-composer-input" type="text" value={text}
               placeholder={lang.textPlaceholder}
               onChange={e => setText(e.target.value)}
               onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleCreate(); } }} />
            <input className="phrase-composer-translation-input" type="text" value={translation}
               placeholder={lang.translationPlaceholder}
               onChange={e => setTranslation(e.target.value)}
               onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleCreate(); } }} />
            <div className={'student-lessons-add-btn' + (!text.trim() || saving ? ' conv-panel-add-btn-disabled' : '')}
               onClick={handleCreate} title={lang.addBtn}>
               <IconAdd />
            </div>
         </div>

         {availableTags.length > 0 ? (
            <div className="phrase-composer-tags">
               {availableTags.map(tag => (
                  <span key={tag.id}
                     className={'phrase-composer-tag-option' + (selectedTagIds.includes(tag.id) ? ' selected' : '')}
                     onClick={() => toggleTag(tag.id)}>
                     {tag.name}
                  </span>
               ))}
            </div>
         ) : ''}

         {text ? (
            <div className="phrase-composer-preview">
               {segments.map((seg, i) => {
                  if (seg.type === 'sep') return <span key={i} className="phrase-token-sep">{seg.value}</span>;
                  const isTyping = midWord && i === lastWordIndex;
                  const status = isTyping ? 'typing' : (wordStatus[seg.value.toLowerCase()] || 'checking');
                  return <span key={i} className={'phrase-token phrase-token-' + status}>{seg.value}</span>;
               })}
            </div>
         ) : ''}

         {suggestions.length > 0 ? (
            <div className="phrase-composer-suggestions">
               <div className="phrase-composer-suggestions-label">{lang.existingLabel}</div>
               {suggestions.map(s => (
                  <div key={s.id} className={'phrase-composer-suggestion-item' + (attachingId === s.id ? ' attaching' : '')}
                     onClick={() => handleAttachExisting(s.id)}>
                     {s.text}
                  </div>
               ))}
            </div>
         ) : ''}
      </div>
   );
}
